import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { BatchResponse } from '@/types/api';
import { emptyStringValues } from '@/utils/objects';

import { InfiniteClips, InfiniteClipsPage, TClip, TPostClipsBatch } from '../types';

import { clipsQueryKeys } from './query-keys';

export async function postClipsBatch(body: TPostClipsBatch): Promise<BatchResponse> {
  return mediaManagerClient.post('/api/v1/clips/batch', emptyStringValues(body));
}

type MutationFn = typeof postClipsBatch;

type TUsePostClipsBatchParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePostClipsBatch({ config }: TUsePostClipsBatchParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: postClipsBatch,
    onError(error) {
      addErrorNotification(
        t('No se ha podido cambiar el estado.'),
        t('Ha habido un error en el proceso.')
      );
      console.error(error);
    },
    onSuccess(data, variables, context) {
      config?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({
        queryKey: clipsQueryKeys.all,
      });

      const idToClipUpdate = new Map(
        variables.action === 'update' ? variables.items.map(item => [item.id, item] as const) : []
      );

      if (idToClipUpdate.size > 0) {
        queryClient.setQueriesData<InfiniteClips | undefined>(
          {
            queryKey: clipsQueryKeys.infinite(),
          },
          previousData => {
            if (!previousData) {
              return undefined;
            }

            const applyStates = (clips: TClip[]) =>
              clips.map(item => ({
                ...item,
                review_state: idToClipUpdate.get(item.id)?.review_state ?? item.review_state,
              }));

            // Pages are flat arrays for `useInfiniteClips` and `{ data, total }` for the cut
            // lists, which need the total for their header. Anything else is left alone rather
            // than mapped blindly, which is what used to throw `page.map is not a function`.
            const applyToPage = (page: InfiniteClipsPage) => {
              if (Array.isArray(page)) return applyStates(page);
              if (page && Array.isArray(page.data))
                return { ...page, data: applyStates(page.data) };
              return page;
            };

            // Every accumulated clip list is written here, most of them holding none of the
            // updated clips. Those keep their previous value: React Query's structural sharing
            // returns the old reference for a deep-equal write, so their subscribers are not
            // woken. Guarding that by hand here was measured to change nothing.
            return {
              ...previousData,
              pages: previousData.pages.map(applyToPage),
            };
          }
        );
      }

      addSuccessNotification(
        t('Se ha cambiado el estado correctamente.'),
        t('El proceso ha sido un éxito.')
      );
    },
  });
}
